// @ts-check
import {merge, propEq, findIndex, filter} from 'ramda'
import cloneDeep from 'lodash/cloneDeep'
import './skills.css'


const DEFAULT_SKILLS = [
  {name: 'Moving', id: 1, jobType: 2},
  {name: 'General Labor', id: 11, jobType: 3},
  {name: 'Warehouse Work', id: 2, jobType: 3},
  {name: 'Pick & Pack', id: 12, jobType: 3},
  {name: 'Event Set-Up', id: 20, jobType: 9},
  {name: 'Event Staff', id: 8, jobType: 9},
  {name: 'Janitorial Work', id: 9, jobType: 10},
  {name: 'Cashier', id: 37, jobType: 9}
];


export class SkillsController {
  /* @ngInject */
  constructor (
    user,
    worker, 
    /** @type {import('../../../shared/reference').ReferenceModule} */
    ReferenceService, 
    /** @type {import('../../../shared/users').UsersModule} */
    UsersService,
    $mdDialog,
    $mdToast
  ) {
    this.user = user
    this.worker = worker
    this.UsersService = UsersService

    this.$mdDialog = $mdDialog
    this.$mdToast = $mdToast

    this.skills = {}
    this.skillsByCategory = {}
    this.refQualificationsBeforeEdit = []
    this.refQualifications = []

    // List of all certs to display in skills section
    Promise.all([
      ReferenceService.getCertifications(),
      UsersService.getCertificationsAdmin(this.worker.external_id),
    ]).then(([refCerts, [workerCerts, drugScreenCertificates]]) => {
      this.refCerts = refCerts
      this.frozenDrugScreenCertificates = drugScreenCertificates

      for (const verifiedCert of workerCerts) {
        // Find index of the current user's cert with regard to the reference cert list and merge data
        const verifiedCertIndex = findIndex(propEq('id', parseInt(verifiedCert.certificateId)))(this.refCerts)
        this.refCerts[verifiedCertIndex] = merge(this.refCerts[verifiedCertIndex], verifiedCert)

        // Set isPresent flag to determine that the checkbox should be ticked
        this.refCerts[verifiedCertIndex].isPresent = true
      }
    });

    // List of all qualifications to display in skills section
    Promise.all([
      ReferenceService.getQualifications(),
      UsersService.getUserQualifications(this.worker.external_id)
    ]).then(([refQualifications, workerQualifications]) => {
      this.refQualifications = refQualifications

      for (const verifiedQualification of workerQualifications) {
        // Find index of the current user's cert with regard to the reference cert list and merge data
        const verificationQualificationIndex = findIndex(propEq('qualificationId', verifiedQualification.qualificationId))(this.refQualifications)
        this.refQualifications[verificationQualificationIndex] = merge(this.refQualifications[verificationQualificationIndex], verifiedQualification)

        // Set isPresent flag to determine that the checkbox should be ticked
        this.refQualifications[verificationQualificationIndex].isPresent = true
      }

      this.refQualificationsBeforeEdit = cloneDeep(refQualifications)
    })

    Promise.all([
      ReferenceService.getSkills(),
      ReferenceService.getSkillCategories()
    ]).then(([skills, categories]) => {

      skills.map(skill => {
        this.skills[skill.id] = {
          id: skill.id,
          name: skill.name,
          job_type_id: skill.job_type_id,
          verified: false
        }
      })

      categories.map(category => {
        this.skillsByCategory[category.id] = {
          id: category.id,
          name: category.name,
          skills: []
        }
      })
      if (typeof this.worker.verifiedSkills === 'undefined' ||
        this.worker.verifiedSkills === null
      ) {
        this.UsersService.getVerifiedSkills(this.worker.id)
        .then(workerSkills => {
          this.setSkillsByCategory(workerSkills)
        })
      } else {
        this.setSkillsByCategory(this.worker.verifiedSkills)
      }
    })
  }

  getSkillsByCategoryArray () {
    return Object.values(this.skillsByCategory);
  }

  setSkillsByCategory (skills) {
    if (skills.length === 0) {
      DEFAULT_SKILLS.forEach(skill => {
        this.skills[skill.id].verified = true
      })
    } else {
      skills.forEach(skill => {
        this.skills[skill.id].verified = true
      })
    }

    for (const index in this.skills) {
      const skill = this.skills[index]
      this.skillsByCategory[skill.job_type_id].skills.push(skill)
    }
  }

  saveSkillsAndQualifications () {
    this.saveQualifications()
    this.saveSkills()
  }

  /**
   * Formats the received certificate from the API to the format that the API expects when saving
   * @param {*} certificate - Certificate as received from the API
   */
  formatFrozenCertificate(certificate) {
    let completed_on;
    let expires_on;

    if (certificate.completedOn) {
      const date = certificate.completedOn
      completed_on = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12))
    }

    if (certificate.expiresOn) {
      const date = certificate.expiresOn
      expires_on = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12))
    }

    return {
      id: certificate.certificateId,
      completed_on,
      expires_on,
      verified: certificate.verified,
      verified_by: certificate.verifiedBy,
      notes: certificate.notes,
    }
  }

  saveSkills () {
    const skills = []
    const skillIds = []

    for (const category in this.skillsByCategory) {
      this.skillsByCategory[category].skills.forEach(skill => {
        if (skill.verified) {
          skills.push(skill)
          skillIds.push(skill.id)
        }
      })
    }

    const verifiedCerts = filter(propEq('isPresent', true), this.refCerts)

    const certsToAdd = []
    for (const verifiedCert of verifiedCerts) {
      const cmosCert = this.toCMOSUserCertification(verifiedCert)
      if (verifiedCert.completedOn) {
        const date = verifiedCert.completedOn
        verifiedCert.completed_on = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12))
        cmosCert.completedOn = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12))
      }

      if (verifiedCert.expiresOn) {
        const date = verifiedCert.expiresOn
        verifiedCert.expires_on = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12))
        cmosCert.expiresOn = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12))
      }
      certsToAdd.push(cmosCert)
    }
    this.UsersService.approveSkills({
      userId: this.worker.external_id,
      skills: JSON.stringify(skillIds),
      certs: [
        ...verifiedCerts, 
        // We freeze drug screen certificates in order to prevent them from getting deleted from the legacy API
        // Drug screen changes are saved in qualifications
        ...(this.frozenDrugScreenCertificates.map(cert => this.formatFrozenCertificate(cert)))
      ]
    })
    .then(() => {
      this.worker.verifiedSkills = skills
      this.UsersService.updateCertificationsAdmin(this.worker.external_id, certsToAdd)
      const msg = 'Verified Skills Updated!'
      this.$mdDialog.hide(skills)
      this.$mdToast.showSimple(msg)
    })
    .catch((err) => {
      if (!err) return
      this.$mdToast.showSimple('Failed To Update Verified Skills')
    })
  }

  saveQualifications () {
    const createdQualifications = []
    const updatedQualifications = []
    const deletedQualifications = [] // Delete API is not implemented yet, but we keep track of deleted qualifications anyways

    for (const [index, qualificationAfterEdit] of this.refQualifications.entries()) {
      const qualificationBeforeEdit = this.refQualificationsBeforeEdit[index];

      if (qualificationBeforeEdit.isPresent && !qualificationAfterEdit.isPresent) {
        deletedQualifications.push(qualificationBeforeEdit)
      } else if (!qualificationBeforeEdit.isPresent && qualificationAfterEdit.isPresent) {
        createdQualifications.push(qualificationAfterEdit)
      } else if (qualificationBeforeEdit.isPresent && qualificationAfterEdit.isPresent) {
        // We don't have any way to determine if a qualification has been updated or not based on the UI
        // So we just create a new qualification if the expiresOn or verification.verifiedAt has changed
        if ((qualificationBeforeEdit.expiresOn?.getTime() !== qualificationAfterEdit.expiresOn?.getTime()) || (qualificationBeforeEdit.verification.verifiedAt?.getTime() !== qualificationAfterEdit.verification.verifiedAt?.getTime())) {
          createdQualifications.push(qualificationAfterEdit)
        } else {
          // There are no fields that can be updated for qualifications at the moment
          // updatedQualifications.push(qualificationAfterEdit)
        }
      }
    }

    const formattedCreatedQualifications = createdQualifications.map(({ isPresent, badge_icon, ...qualification }) => ({
      ...qualification,
      verification: {
        verifiedAt: qualification.verification.verifiedAt,
        verifiedBy: this.user.internalId,
        verifiedType: "ADMIN_LEGACY",
      },
    }));

    const formattedUpdatedQualifications = updatedQualifications.map(({ isPresent, badge_icon, ...qualification }) => qualification);

    if (formattedCreatedQualifications.length > 0) {
      this.UsersService.createUserQualifications(this.worker.external_id, {qualifications: formattedCreatedQualifications })
    }

    if (formattedUpdatedQualifications.length > 0) {
      this.UsersService.updateUserQualifications(this.worker.external_id, {qualifications: formattedUpdatedQualifications })
    }
  }

  cancel () {
    this.$mdDialog.cancel()
  }

  toCMOSUserCertification (cert) {
    return {
      certificateId: cert.id.toString(),
      certificateName: cert.name,
      completedOn: cert.completedOn,
      expiresOn: cert.expiresOn,
      verified: cert.verified === 1,
      verifiedBy: cert.verifiedBy,
      notes: cert.notes,
      certificateUrl: cert.url
    }
  }

}
